<template>
    <div class="relative">
        <div v-if="selectedGame" class="mb-2 flex items-center justify-between rounded border border-slate-200 bg-white px-3 py-2">
            <div class="flex items-center gap-3">
                <div
                    v-if="selectedGame.cover_url_thumb && !selectedGame.cover_url_thumb.match('nocover_qhhlj6.jpg')"
                    class="h-10 w-16 shrink-0 rounded border border-slate-200 bg-cover bg-center"
                    :style="{
                        'background-image': `url(${selectedGame.cover_url_thumb})`,
                    }"
                />
                <div v-else class="flex h-10 w-16 shrink-0 rounded bg-neutral-200">
                    <span class="m-auto block text-center text-xs leading-4">No<br />Box Art</span>
                </div>
                <div>
                    <div class="text-sm font-medium text-slate-900">
                        {{ selectedGame.name }}
                        <span v-if="selectedGame.first_release_date">
                            ({{ $moment(selectedGame.first_release_date).format('YYYY') }})
                        </span>
                    </div>
                    <div v-if="selectedGame._platforms" class="mt-1 flex items-center gap-1.5">
                        <AppIcon
                            v-if="selectedGame._platforms.twitch?.id && typeof selectedGame._platforms.twitch.id === 'string'"
                            name="twitch"
                            class="h-3.5 w-3.5 text-twitch"
                            :title="'Available on Twitch'"
                        />
                        <AppIcon
                            v-if="selectedGame._platforms.youtube?.id && typeof selectedGame._platforms.youtube.id === 'string'"
                            name="youtube"
                            class="h-3.5 w-3.5 text-youtube"
                            :title="'Available on YouTube'"
                        />
                        <span
                            v-if="(!selectedGame._platforms.twitch?.id || typeof selectedGame._platforms.twitch.id !== 'string') && (!selectedGame._platforms.youtube?.id || typeof selectedGame._platforms.youtube.id !== 'string')"
                            class="text-xs text-slate-400"
                            :title="'Not available on Twitch or YouTube'"
                        >
                            —
                        </span>
                    </div>
                </div>
            </div>
            <button
                type="button"
                class="ml-2 text-slate-400 hover:text-slate-600"
                @click="clearSelection"
            >
                <AppIcon name="xmark" class="h-4 w-4" />
            </button>
        </div>

        <div v-if="!selectedGame" class="relative">
            <input
                ref="trigger"
                v-model="searchText"
                class="form-input w-full rounded-b-none rounded-t"
                :placeholder="searchPlaceholder || 'Start typing the name of your game...'"
                aria-haspopup="true"
                :aria-expanded="dropdownOpen"
                type="text"
                :class="{
                    'rounded-bl rounded-br': !dropdownOpen,
                }"
                @click.prevent="dropdownOpen = true"
                @focus="onInputFocus"
            />

            <transition
                enter-active-class="transition ease-out duration-100 transform"
                enter-from-class="opacity-0 -translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-out duration-100"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div
                    v-show="dropdownOpen"
                    class="absolute left-0 top-full z-10 w-full rounded-bl rounded-br border-b border-l border-r border-slate-200 bg-white"
                >
                    <div ref="dropdown" class="max-h-[400px] overflow-y-auto shadow-lg">
                        <div v-if="state.isLoading" class="px-4 py-3 text-center text-sm text-slate-500 flex justify-center">
                            <AppIcon
                                name="spinner-third"
                                class="h-4 w-4 animate-spin"
                            />
                            <span class="ml-2">Searching...</span>
                        </div>

                        <div v-else class="divide-y border-slate-200">
                            <div
                                v-for="game in games"
                                :key="game._id"
                                class="cursor-pointer hover:bg-slate-100"
                                @click="onGameSelect(game)"
                            >
                                <div class="flex items-center px-3 py-2">
                                    <div class="h-10 w-16 shrink-0 rounded">
                                        <div
                                            v-if="game.cover_url_thumb && !game.cover_url_thumb.match('nocover_qhhlj6.jpg')"
                                            class="h-full w-full rounded border border-slate-100 bg-cover bg-center"
                                            :style="{
                                                'background-image': `url(${game.cover_url_thumb})`,
                                            }"
                                        />
                                        <div v-else class="flex h-full w-full rounded bg-neutral-200">
                                            <span class="m-auto block text-center text-xs leading-4">No<br />Box Art</span>
                                        </div>
                                    </div>
                                    <div class="ml-3 flex flex-1 items-center justify-between">
                                        <div class="my-auto text-sm leading-none">
                                            <div>
                                                <span class="font-medium">{{ game.name }}</span>
                                                <span v-if="game.first_release_date">
                                                    ({{ $moment(game.first_release_date).format('YYYY') }})
                                                </span>
                                            </div>
                                            <div v-if="game.involved_companies" class="mt-0.5 text-xs text-slate-600">
                                                {{ $lodash.map(game.involved_companies, 'company.name').reverse().join(', ') }}
                                            </div>
                                        </div>
                                        <div v-if="game._platforms" class="ml-2 flex items-center gap-1.5">
                                            <AppIcon
                                                v-if="game._platforms.twitch?.id && typeof game._platforms.twitch.id === 'string'"
                                                name="twitch"
                                                class="h-3.5 w-3.5 text-twitch"
                                                :title="'Available on Twitch'"
                                            />
                                            <AppIcon
                                                v-if="game._platforms.youtube?.id && typeof game._platforms.youtube.id === 'string'"
                                                name="youtube"
                                                class="h-3.5 w-3.5 text-youtube"
                                                :title="'Available on YouTube'"
                                            />
                                            <span
                                                v-if="(!game._platforms.twitch?.id || typeof game._platforms.twitch.id !== 'string') && (!game._platforms.youtube?.id || typeof game._platforms.youtube.id !== 'string')"
                                                class="text-xs text-slate-400"
                                                :title="'Not available on Twitch or YouTube'"
                                            >
                                                —
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-if="!state.isLoading && games.length === 0 && searchText" class="p-4 text-center text-sm text-slate-500">
                                No games found
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import _ from 'lodash'
import moment from 'moment'
import { axios } from '@/utils/axios'

export default {
    name: 'IGDBGameSelect',

    props: {
        selectedOptions: {
            type: Array,
            default: () => [],
        },
        searchPlaceholder: {
            type: String,
            default: 'Start typing the name of your game...',
        },
    },

    emits: ['selection-change'],

    setup() {
        const dropdownOpen = ref(false)
        const dropdown = ref(null)
        const trigger = ref(null)
        const searchText = ref('')

        // Close on click outside
        const clickHandler = ({ target }) => {
            if (!dropdownOpen.value || dropdown.value?.contains(target) || trigger.value?.contains(target)) return
            dropdownOpen.value = false
        }

        // Close if the esc key is pressed
        const keyHandler = ({ keyCode }) => {
            if (!dropdownOpen.value || keyCode !== 27) return
            dropdownOpen.value = false
        }

        onMounted(() => {
            document.addEventListener('click', clickHandler)
            document.addEventListener('keydown', keyHandler)
        })

        onUnmounted(() => {
            document.removeEventListener('click', clickHandler)
            document.removeEventListener('keydown', keyHandler)
        })

        return {
            dropdownOpen,
            dropdown,
            trigger,
            searchText,
        }
    },

    data() {
        return {
            games: [],
            state: {
                isLoading: false,
            },
            selectedGame: null,
        }
    },

    created() {
        // Initialize selected game from props
        if (this.selectedOptions && this.selectedOptions.length > 0) {
            const gameOption = this.selectedOptions[0]
            // Convert CodeSelect format to IGDB format if needed
            if ((typeof gameOption === 'string' && !isNaN(parseInt(gameOption))) || typeof gameOption === 'number') {
                this.loadIndividualGame(gameOption)
            } else {
                if (gameOption.missingIGDB) {
                    this.findIGDBGameFromPlatforms(gameOption.platforms)
                }
                else if (gameOption.id && gameOption.name) {
                    this.selectedGame = {
                        _id: gameOption.id,
                        name: gameOption.name,
                        label: gameOption.label || gameOption.name,
                        cover_url_thumb: gameOption.cover_url_thumb || null,
                        first_release_date: gameOption.metadata?.release_year ? moment(`${gameOption.metadata.release_year}-01-01`).toDate() : null,
                        _platforms: gameOption._platforms || {},
                    }
                }
            }
        }

        // Create debounced search function
        this.debouncedSearch = _.debounce((searchValue) => {
            if (this.dropdownOpen || searchValue) {
                this.gameSearchProvider(searchValue || '', true)
            }
        }, 500)
    },

    watch: {
        selectedOptions(newValue) {
            if (newValue && newValue.length > 0) {
                const gameOption = newValue[0]

                if (gameOption.id && gameOption.name) {
                        this.selectedGame = {
                            _id: gameOption.id,
                            name: gameOption.name,
                            label: gameOption.label || gameOption.name,
                            cover_url_thumb: gameOption.cover_url_thumb || null,
                            first_release_date: gameOption.metadata?.release_year ? moment(`${gameOption.metadata.release_year}-01-01`).toDate() : null,
                            _platforms: gameOption._platforms || {},
                        }
                    }
            } else {
                this.selectedGame = null
            }
        },
        // Watch searchText and trigger debounced search when it changes
        searchText(newValue) {
            if (this.debouncedSearch) {
                this.debouncedSearch(newValue)
            }
        },
    },

    methods: {
        onInputFocus() {
            if (this.searchText && this.games.length === 0) {
                this.gameSearchProvider(this.searchText, true)
            } else if (!this.searchText) {
                this.gameSearchProvider('', true)
            }
        },

        async gameSearchProvider(searchText, isOpenedAfter) {
            this.games = []
            this.state.isLoading = true

            let endpoint = '/games/igdb/search'
            if (!searchText) endpoint = '/games/igdb/search/local'

            try {
                const { data } = await this.$axios.post(endpoint, {
                    text: searchText.toLowerCase(),
                })

                this.state.isLoading = false
                this.games = data.payload.games || []

                if (isOpenedAfter) {
                    this.dropdownOpen = true
                }
            } catch (error) {
                console.error('Error searching for game', error)
                this.state.isLoading = false
                this.games = []
            }
        },

        onGameSelect(game) {
            // Convert IGDB game format to CodeSelect-compatible format
            const gameOption = {
                id: game._id,
                value: game._id,
                name: game.name,
                label: game.name,
                igdb_id: game._id,
                cover_url_thumb: game.cover_url_thumb,
                first_release_date: game.first_release_date,
                _platforms: game._platforms || {},
                alternative_names: game.alternative_names || [], // Include alternate names for hashtag conversion
                metadata: {
                    release_year: game.first_release_date ? moment(game.first_release_date).format('YYYY') : null,
                },
            }

            this.selectedGame = game
            this.games = []
            this.searchText = ''
            this.dropdownOpen = false

            // Emit in CodeSelect format (array with single item)
            this.$emit('selection-change', [gameOption])
        },

        loadIndividualGame(igdbGameId) {
            return axios
                .get(`/games/igdb/search/local/${igdbGameId}`)
                .then(({data}) => {
                    if (data?.payload._id) {
                        const game = data.payload
                        this.onGameSelect(game)
                    }
            })
            .catch((error) => {
                console.log(error)
            })
        },

        findIGDBGameFromPlatforms(platforms) {
            return axios
                .post('/games/igdb/search/findbyplatform', {
                    platforms : platforms
                })
                .then(({data}) => {
                    if (data?.payload._id) {
                        const game = data.payload
                        this.onGameSelect(game)
                    }
            })
            .catch((error) => {
                console.log(error)
            })
        },

        clearSelection() {
            this.selectedGame = null
            this.searchText = ''
            this.$emit('selection-change', [])
        },
    },
}
</script>

